import React, { HTMLProps, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { modelPortfolioUrls, SELECTED_FUND_COLORS } from '../../../../config/constants';
import { IconElements } from '../../../../config/icons';
import { FundRatesContext } from '../../../../contexts/funds-robot/FundRatesContext';
import { FundsSelectionContext } from '../../../../contexts/funds-robot/FundsSelectionContext';
import { Asset } from '../../../../types/Asset';
import { toPrice, toPercentage } from '../../../../utils/parsers';
import { ComparisonScreenFundSelectionContext } from '../ComparisonScreen';
import { ComparisonStickyCardDetails } from './ComparisonStickyCardDetails';
import { Link } from '../../../general/Link';

export const ComparisonStickyCard: React.FC<{} & HTMLProps<HTMLDivElement>> = ({ children, className }) => {
    const fundsContext = useContext(FundsSelectionContext);
    const { selectedFund, setSelectedFund } = useContext(ComparisonScreenFundSelectionContext);
    const fundRatesContext = useContext(FundRatesContext);
    const { t } = useTranslation('funds');

    if (!selectedFund) {
        return <div></div>;
    }

    const rates = fundRatesContext.rates.get(selectedFund.id);

    return (
        <div
            className={`w-96 2xl:w-128 rounded-lg sticky top-24 pb-8 ${className}`}
            style={{
                height: '90vh',
                overflowY: 'scroll',
            }}
        >
            <>
                <div className="bg-white shadow-lg rounded-t-lg overflow-hidden">
                    <div className="p-8">
                        <div className="text-black text-3xl 2xl:text-4xl font-medium">{selectedFund?.name}</div>
                        {(selectedFund?.category === 'Stock' || selectedFund?.category === 'Bond') && (
                            <>
                                {rates?.withinInterval.last && (
                                    <div className="flex flex-row justify-between items-center mt-8">
                                        <div>
                                            <div className="text-lg font-medium">{t('funds-comparison-screen-rate')}</div>

                                            <div>
                                                {rates?.withinInterval.last.date
                                                    .setLocale('nl-NL')
                                                    .toFormat('dd MMMM yyyy')}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="text-5xl font-medium flex justify-center items-center">
                                                <span>{toPrice(rates?.withinInterval.last.raw)}</span>
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        {rates?.withinInterval.last && rates?.withinInterval.first && (
                            <div className="flex flex-col 2xl:flex-row gap-4 justify-between items-center mt-8">
                                <div className="self-start 2xl:self-center">
                                    <div className="text-lg font-medium">{t('funds-comparison-screen-yield')}</div>
                                    <div>
                                        {rates?.withinInterval.first.date.setLocale('nl-NL').toFormat('dd MMMM yyyy')} -{' '}
                                        {rates?.withinInterval.last.date.setLocale('nl-NL').toFormat('dd MMMM yyyy')}
                                    </div>
                                </div>
                                <div className="self-start 2xl:self-center">
                                    <span
                                        className={`${
                                            rates.withinInterval.last.indexed - 100 >= 0
                                                ? 'text-bnd-green'
                                                : 'text-bnd-black-70'
                                        } text-4xl font-medium flex justify-center items-center`}
                                    >
                                        <span className="mr-4">
                                            {toPercentage(rates?.withinInterval.last.indexed - 100)}
                                        </span>
                                        <IconElements.ArrowRising
                                            className={`${
                                                rates.withinInterval.last.indexed - 100 >= 0
                                                    ? 'text-bnd-green'
                                                    : 'text-bnd-black-70'
                                            }`}
                                            style={{
                                                transform:
                                                    rates.withinInterval.last.indexed - 100 >= 0
                                                        ? 'scaleY(1)'
                                                        : 'scaleY(-1)',
                                            }}
                                        />
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex flex-row text-sm">
                    {fundsContext.funds.selected.length > 1 &&
                        fundsContext.funds.selected.map((fund, i) => {
                            return (
                                <div
                                    className={`flex-1 p-4 truncate text-center text-bnd-yellow bg-white shadow-lg rounded-b-lg cursor-pointer
                                        ${selectedFund?.id === fund.id ? 'underline' : 'shadow-inner opacity-60'}
                                        ${i === fundsContext.funds.selected.length - 1 ? '' : 'mr-0.5'}`}
                                    style={{ color: SELECTED_FUND_COLORS[i] }}
                                    onClick={() => {
                                        setSelectedFund(fund);
                                    }}
                                    key={i}
                                >
                                    {fund.name}
                                </div>
                            );
                        })}
                </div>

                <ComparisonStickyCardDetails asset={selectedFund} className="mt-16" />

                <DocumentationLinks asset={selectedFund} className="mt-12" />
                {children}
            </>
        </div>
    );
};

const ComparisonStickyCardLink: React.FC<{} & HTMLProps<HTMLAnchorElement>> = ({ children, className, ...rest }) => {
    return (
        <Link className={`block text-bnd-green ${className}`} target="_blank" {...rest} linkColor="green">
            {children}
        </Link>
    );
};

export const DocumentationLinks: React.FC<{ asset: Asset } & HTMLProps<HTMLDivElement>> = ({
    asset,
    className,
    ...rest
}) => {
    const { t } = useTranslation('funds');
    return (
        <div className={`${className}`} {...rest}>
            <p>Bekijk hieronder de documentatie:</p>
            {asset.category === 'Stock' && asset.urls.umbrellaFundInformation && (
                <ComparisonStickyCardLink href={asset.urls.umbrellaFundInformation}>
                    {t('funds-comparison-screen-link-first-umbrella')}
                </ComparisonStickyCardLink>
            )}
            {asset.category === 'Bond' && asset.urls.umbrellaFundInformation && (
                <ComparisonStickyCardLink href={asset.urls.umbrellaFundInformation}>
                    {t('funds-comparison-screen-link-second-umbrella')}
                </ComparisonStickyCardLink>
            )}
            {asset.category !== 'Portfolio' && asset.urls.essentialInvestmentInformation && (
                <ComparisonStickyCardLink href={asset.urls.essentialInvestmentInformation}>
                    {t('funds-comparison-screen-link-investment-information')} {asset.name}
                </ComparisonStickyCardLink>
            )}
            {asset.category !== 'Portfolio' && asset.urls.factsheetInformation && (
                <ComparisonStickyCardLink href={asset.urls.factsheetInformation}>
                    {t('funds-comparison-screen-link-factsheet-information')} {asset.name}
                </ComparisonStickyCardLink>
            )}

            {asset.category === 'Portfolio' && (
                <ComparisonStickyCardLink href={modelPortfolioUrls.modelInvestment}>
                    {t('funds-comparison-screen-link-model-investment')}
                </ComparisonStickyCardLink>
            )}
            {asset.category === 'Portfolio' && (
                <ComparisonStickyCardLink href={modelPortfolioUrls.firstUmbrellaFund}>
                    {t('funds-comparison-screen-link-first-umbrella')}
                </ComparisonStickyCardLink>
            )}
            {asset.category === 'Portfolio' && (
                <ComparisonStickyCardLink href={modelPortfolioUrls.secondUmbrellaFund}>
                    {t('funds-comparison-screen-link-second-umbrella')}
                </ComparisonStickyCardLink>
            )}
        </div>
    );
};
